/**
 * ProductListPage — Angular `ProductListComponent` parity; layout aligned with User Management / other admin lists.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Loader2,
  Plus,
  Trash2,
  ChevronDown,
  X,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { FilterToolbarSearch } from '@/components/ui/FilterToolbarSearch'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { EditIconButton } from '@/components/ui/EditIconButton'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { LIBERTY_FOCUS_INPUT } from '@/features/user-management/utils/libertyAdminUi'
import { useUserRole } from '@/hooks/useUserRole'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { showSuccess, showProductApiError } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import styles from '@/features/invitee/styles/invitesParity.module.css'
import { ProductTableSortColumnHeader } from '../components/ProductTableSortHeader'
import '../styles/productTable.css'
import { PRODUCT_POLICIES, PRODUCT_PER_PAGE_OPTIONS } from '../constants'
import { productConfirmPresentation } from '../constants/productDialogProps'
import { useProductList } from '../hooks/useProductList'
import type { ProductListItem } from '../types'
import { deleteProduct, fetchCanDeleteProductMessage } from '../services/productApi'
import { productDescriptionPreview } from '../utils/productFormatters'

/** Truncated preview + InfoPopover full text — same pattern as cron job descriptions. */
function ProductDescriptionCell({
  text,
  isLastRow,
}: {
  text: string
  isLastRow: boolean
}) {
  const full = text ?? ''
  const preview = productDescriptionPreview(full)
  const previewDisplay = preview && !preview.endsWith('...') ? `${preview}...` : preview

  if (!preview) {
    return <td className="overflow-hidden text-slate-700">—</td>
  }

  return (
    <td className="overflow-hidden text-slate-700">
      <InfoPopover
        inline
        content={full}
        align="start"
        maxWidth={448}
        isLastRow={isLastRow}
        testId="product-description-popover"
        contentClassName="whitespace-pre-wrap text-sm leading-relaxed"
      >
        <button
          type="button"
          className="block w-full max-w-full cursor-default border-0 bg-transparent p-0 text-left font-inherit text-inherit text-slate-700"
          aria-label="Show full description"
        >
          <span className="block truncate">{previewDisplay}</span>
        </button>
      </InfoPopover>
    </td>
  )
}

function PaginationControls({
  currentPage,
  totalPages,
  onChangePage,
  testIdPrefix,
}: {
  currentPage: number
  totalPages: number
  onChangePage: (p: number) => void
  testIdPrefix: string
}) {
  const btn =
    'min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 enabled:hover:bg-slate-50 disabled:opacity-50 text-sm'
  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end" data-testid={`${testIdPrefix}-pagination`}>
      <button type="button" className={btn} disabled={currentPage === 1} onClick={() => onChangePage(1)}>
        &laquo;
      </button>
      <button
        type="button"
        className={btn}
        disabled={currentPage === 1}
        onClick={() => onChangePage(currentPage - 1)}
      >
        &lsaquo;
      </button>
      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
        let p: number
        if (totalPages <= 5) p = i + 1
        else if (currentPage <= 3) p = i + 1
        else if (currentPage >= totalPages - 2) p = totalPages - 4 + i
        else p = currentPage - 2 + i
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChangePage(p)}
            className={cn(
              'min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium border',
              p === currentPage
                ? 'bg-[rgb(21_151_255)] text-white border-[rgb(21_151_255)]'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
            )}
          >
            {p}
          </button>
        )
      })}
      <button
        type="button"
        className={btn}
        disabled={currentPage === totalPages}
        onClick={() => onChangePage(currentPage + 1)}
      >
        &rsaquo;
      </button>
      <button
        type="button"
        className={btn}
        disabled={currentPage === totalPages}
        onClick={() => onChangePage(totalPages)}
      >
        &raquo;
      </button>
    </div>
  )
}

/**
 * "Go to page" number input (Angular `#gotoPage (change)="paginateItem(value)"`, shown when `last > 1`).
 * Uncontrolled, keyed on `current` so it resyncs on external page changes; commits on Enter/blur and
 * clamps to [1, last]; non-numeric input resets to the current page.
 */
function ProductGotoPageInput({
  current,
  last,
  onChangePage,
  testId,
}: {
  current: number
  last: number
  onChangePage: (p: number) => void
  testId: string
}) {
  return (
    <input
      type="number"
      min={1}
      max={last}
      aria-label="Go to page"
      data-testid={testId}
      key={`${testId}-${current}`}
      defaultValue={current}
      className="w-14 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-center text-xs font-bold text-slate-900 tabular-nums focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20 sm:text-sm"
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      onBlur={(e) => {
        const parsed = parseInt(e.target.value, 10)
        if (Number.isNaN(parsed)) {
          e.target.value = String(current)
          return
        }
        const next = Math.min(last, Math.max(1, parsed))
        if (next !== current) onChangePage(next)
        e.target.value = String(next)
      }}
    />
  )
}

function ProductListPaginationBar({
  pageMeta,
  totalPages,
  queryPerPage,
  onChangePage,
  onChangePerPage,
  testIdPrefix,
}: {
  pageMeta: { current: number; total: number; last: number }
  totalPages: number
  queryPerPage: number
  onChangePage: (p: number) => void
  onChangePerPage: (n: number) => void
  testIdPrefix: string
}) {
  const currentPage = pageMeta.current || 1
  return (
    <div className="flex min-w-0 flex-col gap-2 border-slate-100 bg-slate-50/50 px-3 py-2.5 sm:gap-3 sm:px-6 sm:py-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-center gap-1.5 text-center text-xs font-medium text-slate-500 sm:justify-start sm:text-left sm:text-sm lg:max-w-md">
        <span>Page</span>
        {(pageMeta.last || 1) > 1 ? (
          <ProductGotoPageInput
            current={currentPage}
            last={pageMeta.last || 1}
            onChangePage={onChangePage}
            testId={`${testIdPrefix}-page-jump`}
          />
        ) : (
          <span className="font-bold text-slate-900">{currentPage}</span>
        )}
        <span>
          of <span className="font-bold text-slate-900">{pageMeta.last || 1}</span>
        </span>
        <span className="mx-2 text-slate-300">|</span>
        <span>
          Total <span className="font-bold text-slate-900">{pageMeta.total}</span> Records
        </span>
      </div>
      <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-2 lg:ml-auto lg:flex-1">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onChangePage={onChangePage}
          testIdPrefix={testIdPrefix}
        />
        <div className="flex shrink-0 items-center justify-center gap-1.5 sm:justify-end sm:gap-2 sm:pl-0 sm:ml-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rows</span>
          <select
            className="min-w-0 rounded-lg border border-slate-200 bg-white p-1.5 text-xs text-slate-700 sm:p-2 sm:text-sm focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
            value={queryPerPage}
            onChange={(e) => onChangePerPage(parseInt(e.target.value, 10))}
            data-testid={`${testIdPrefix}-per-page-select`}
          >
            {PRODUCT_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

function columnAriaSort(
  column: string,
  orderBy: string,
  sortBy: 'asc' | 'desc',
): 'ascending' | 'descending' | 'none' {
  if (orderBy !== column) return 'none'
  return sortBy === 'asc' ? 'ascending' : 'descending'
}

export function ProductListPage() {
  const navigate = useNavigate()
  const { hasPolicy } = useUserRole()

  const {
    rows,
    loading,
    error,
    query,
    searchInput,
    setSearchInput,
    statusFilter,
    handleStatusChange,
    pageMeta,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
  } = useProductList()

  const canList = hasPolicy([PRODUCT_POLICIES.LISTING])
  const canAdd = hasPolicy([PRODUCT_POLICIES.ADD])
  const canEdit = () => hasPolicy([PRODUCT_POLICIES.EDIT])
  const canDelete = (p: ProductListItem) =>
    hasPolicy([PRODUCT_POLICIES.DELETE]) && p.product_id !== 1

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteMessage, setDeleteMessage] = useState('')
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const totalPages = Math.max(1, pageMeta.last || 1)
  const statusFilterLabel = statusFilter === '1' ? 'Active' : statusFilter === '0' ? 'Inactive' : ''
  const hasActiveFilters = Boolean(statusFilter) || Boolean(query.search)

  // Immediate filter apply on submit ("Done" or Enter)
  const handleFilterSubmit = () => {
    applyFilters()
  }

  const handleResetAllFilters = () => {
    resetFilters()
  }

  async function onDeleteClick(productId: number) {
    setDeleteBusy(true)
    try {
      const msg = await fetchCanDeleteProductMessage(productId)
      if (msg) {
        setDeleteMessage(msg)
        setDeleteTargetId(productId)
        setDeleteConfirmOpen(true)
      } else {
        showProductApiError('Cannot delete this product as it is assigned to properties.')
      }
    } catch (e) {
      showProductApiError(e)
    } finally {
      setDeleteBusy(false)
    }
  }

  async function confirmDelete() {
    if (deleteTargetId == null) return
    setDeleteBusy(true)
    try {
      await deleteProduct(deleteTargetId)
      showSuccess('Product Deleted Successfully!')
      setDeleteConfirmOpen(false)
      setDeleteTargetId(null)
      refetch()
    } catch (e) {
      showProductApiError(e)
    } finally {
      setDeleteBusy(false)
    }
  }

  if (!canList) {
    return (
      <div className="min-h-screen bg-[#f8fafc] pb-12 font-sans text-slate-900">
        <PageHeader title="PRODUCTS" />
        <div className="w-full px-3 py-6 sm:px-6">
          <p className="text-slate-600">You do not have permission to view products.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen min-w-0 bg-[#f8fafc] pb-12 font-sans text-slate-900" data-testid="product-list-page">
      <PageHeader title="PRODUCTS" testIdRoot="product-page-header" />

      <div className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8">
        {/* Inline toolbar: search (500ms debounced) | status filter (auto-applied) | filter chip | clear | count | add. */}
        <form
          data-testid="product-list-toolbar"
          className="flex w-full min-w-0 flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-nowrap sm:gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            handleFilterSubmit()
          }}
        >
          <FilterToolbarSearch
            data-testid="product-list-search-wrap"
            inputDataTestId="product-list-search-input"
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search products"
            label="Search"
            wrapClassName="min-w-0 max-w-sm flex-1"
            inputClassName="focus:border-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]/15"
          />
          <div className="relative shrink-0">
            <select
              id="product-list-status"
              aria-label="Status"
              className={cn(
                'h-[42px] appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-8 text-sm leading-none shadow-sm',
                LIBERTY_FOCUS_INPUT,
              )}
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              data-testid="product-list-status"
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
              aria-hidden
            />
          </div>
          {statusFilterLabel && (
            <span
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[rgb(21,151,255)]/30 bg-[rgb(21,151,255)]/10 px-3 py-2 text-sm font-semibold text-[rgb(21_151_255)]"
              data-testid="product-list-status-chip"
            >
              {statusFilterLabel}
              <button
                type="button"
                onClick={() => handleStatusChange('')}
                aria-label={`Remove ${statusFilterLabel} filter`}
                className="rounded-full p-0.5 hover:bg-[rgb(21,151,255)]/20"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </span>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleResetAllFilters}
              className="shrink-0 text-sm font-semibold text-[rgb(21_151_255)] hover:underline"
              data-testid="product-list-reset-button"
            >
              Clear filters
            </button>
          )}
          <button type="submit" disabled={loading} className="sr-only" data-testid="product-list-filter-submit">
            Apply
          </button>
          <span
            className="ml-auto shrink-0 whitespace-nowrap text-sm text-slate-500"
            data-testid="product-list-count"
          >
            <span className="font-semibold text-slate-900">{pageMeta.total}</span> products
          </span>
          {canAdd && (
            <Link
              to="/product/add"
              className={cn(primaryEntityActionButtonClass, 'shrink-0')}
              data-testid="product-list-add-button"
            >
              <Plus className="h-5 w-5 shrink-0" aria-hidden />
              Add New Product
            </Link>
          )}
        </form>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
          </div>
        )}

        {!loading && !error && (
          <div className={styles.tableCard} data-testid="product-list-table-card">
            <ProductListPaginationBar
              pageMeta={pageMeta}
              totalPages={totalPages}
              queryPerPage={query.perPage}
              onChangePage={changePage}
              onChangePerPage={changePerPage}
              testIdPrefix="product-list-top"
            />

            <div
              className={cn(styles.tableResponsiveScroll, 'touch-scroll')}
              data-testid="product-list-table-container"
            >
              <table
                data-testid="product-list-table"
                className={cn(
                  styles.listingTable,
                  styles.propertyListingTable,
                  'table-fixed w-full [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal',
                  '[&_tbody_td:not([colspan])]:break-words',
                  '[&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle',
                  '[&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug',
                )}
              >
                <colgroup>
                  <col className="column-product-id" style={{ width: '120px' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <ProductTableSortColumnHeader
                      column="product_id"
                      orderBy={query.orderBy}
                      sortBy={query.sortBy}
                      onSort={sort}
                      align="start"
                      className="column-product-id"
                      data-testid="product-list-sort-product-id"
                      aria-sort={columnAriaSort('product_id', query.orderBy, query.sortBy)}
                    >
                      Product ID
                    </ProductTableSortColumnHeader>
                    <ProductTableSortColumnHeader
                      column="name"
                      orderBy={query.orderBy}
                      sortBy={query.sortBy}
                      onSort={sort}
                      align="start"
                      data-testid="product-list-sort-name"
                      aria-sort={columnAriaSort('name', query.orderBy, query.sortBy)}
                    >
                      Product Name
                    </ProductTableSortColumnHeader>
                    {/* Description is not sortable (Angular renders a plain <th>). */}
                    <th
                      scope="col"
                      className="column-description !text-left !align-middle"
                      data-testid="product-list-column-description-header"
                    >
                      Description
                    </th>
                    <ProductTableSortColumnHeader
                      column="allow_dynamic_access_fee"
                      orderBy={query.orderBy}
                      sortBy={query.sortBy}
                      onSort={sort}
                      align="start"
                      data-testid="product-list-sort-fee"
                      aria-sort={columnAriaSort('allow_dynamic_access_fee', query.orderBy, query.sortBy)}
                    >
                      Dynamic Access Fee
                    </ProductTableSortColumnHeader>
                    <ProductTableSortColumnHeader
                      column="is_active"
                      orderBy={query.orderBy}
                      sortBy={query.sortBy}
                      onSort={sort}
                      align="start"
                      data-testid="product-list-sort-status"
                      aria-sort={columnAriaSort('is_active', query.orderBy, query.sortBy)}
                    >
                      Status
                    </ProductTableSortColumnHeader>
                    <th
                      scope="col"
                      className="!text-center !align-middle"
                      data-testid="product-list-column-action-header"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="!py-16 text-center text-slate-500">
                        No Products Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((p, rowIndex) => (
                      <tr key={p.product_id} data-testid={`product-list-row-${p.product_id}`} className="transition-colors">
                        <td
                          className="column-product-id whitespace-nowrap tabular-nums text-slate-700"
                          data-testid={`product-list-row-${p.product_id}-id`}
                        >
                          {p.product_id}
                        </td>
                        <td>
                          <Link
                            to={`/product/edit/${p.product_id}`}
                            className="text-[13px] font-medium text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline"
                            data-testid={`product-list-row-${p.product_id}-name-link`}
                          >
                            {p.name}
                          </Link>
                          {p.product_id === 1 && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-300">
                              Default
                            </span>
                          )}
                        </td>
                        <ProductDescriptionCell
                          text={p.description ?? ''}
                          isLastRow={rowIndex === rows.length - 1}
                        />
                        <td>
                          <span
                            className={cn(
                              'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
                              p.allow_dynamic_access_fee === 1
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-rose-100 text-rose-700',
                            )}
                          >
                            {p.allow_dynamic_access_fee === 1 ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={cn(
                              'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
                              p.is_active === 1
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-red-100 text-red-700',
                            )}
                          >
                            {p.is_active === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="!text-center">
                          <div className="mx-auto flex w-[4.25rem] items-center justify-start gap-1">
                            {canEdit() && (
                              <EditIconButton
                                onClick={() =>
                                  navigate(`/product/edit/${p.product_id}`)
                                }
                                data-testid={`product-list-row-${p.product_id}-edit-link`}
                              />
                            )}
                            {canDelete(p) && (
                              <button
                                type="button"
                                title="Delete"
                                aria-label="Delete"
                                disabled={deleteBusy}
                                onClick={() => void onDeleteClick(p.product_id)}
                                className="inline-flex items-center justify-center rounded-full bg-transparent p-1 text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:cursor-not-allowed disabled:opacity-50"
                                data-testid={`product-list-row-${p.product_id}-delete-button`}
                              >
                                <Trash2 className="h-4 w-4" aria-hidden />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ProductListPaginationBar
              pageMeta={pageMeta}
              totalPages={totalPages}
              queryPerPage={query.perPage}
              onChangePage={changePage}
              onChangePerPage={changePerPage}
              testIdPrefix="product-list-bottom"
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete product"
        message={deleteMessage || 'Are you sure you want to delete this product?'}
        primaryLabel="Delete"
        onPrimary={() => void confirmDelete()}
        secondaryLabel="Cancel"
        onSecondary={() => {
          setDeleteConfirmOpen(false)
          setDeleteTargetId(null)
        }}
        onDismiss={() => {
          setDeleteConfirmOpen(false)
          setDeleteTargetId(null)
        }}
        variant="warning"
        {...productConfirmPresentation}
      />
    </div>
  )
}
